import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEmailLogs, resendEmail } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  Mail,
  Filter,
  Eye,
  Send,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function EmailLogsScreen() {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    status: "",
    search: "",
    startDate: "",
    endDate: "",
    page: 1,
    limit: 20,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, emailLog: null });

  const { data, isLoading } = useQuery({
    queryKey: ["email-logs", filters],
    queryFn: () => getEmailLogs(filters),
  });

  const resendMutation = useMutation({
    mutationFn: resendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries(["email-logs"]);
      toast({
        title: "Başarılı",
        description: "Email yeniden gönderildi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Email gönderilemedi",
        variant: "destructive",
      });
    },
  });

  const handleResend = (emailLog) => {
    if (window.confirm("Bu email'i yeniden göndermek istediğinizden emin misiniz?")) {
      resendMutation.mutate(emailLog.id);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SENT: { variant: "success", label: "Gönderildi", icon: CheckCircle },
      FAILED: { variant: "destructive", label: "Başarısız", icon: XCircle },
      PENDING: { variant: "warning", label: "Beklemede", icon: Clock },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center w-fit gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const emailLogs = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  return (
    <AdminLayout
      title="Email Logs"
      description="Email gönderim geçmişini görüntüleyin"
      loading={isLoading}
    >
      <div className="space-y-6">
        {/* Quick Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Email</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.total || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Filtreler</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {showFilters ? "Gizle" : "Göster"}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Durum</Label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value === "all" ? "" : value, page: 1 })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tüm durumlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="SENT">Gönderildi</SelectItem>
                      <SelectItem value="FAILED">Başarısız</SelectItem>
                      <SelectItem value="PENDING">Beklemede</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters({ ...filters, startDate: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters({ ...filters, endDate: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Ara (Email veya Konu)</Label>
                  <Input
                    placeholder="Ara..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setFilters({
                        status: "",
                        search: "",
                        startDate: "",
                        endDate: "",
                        page: 1,
                        limit: 20,
                      })
                    }
                  >
                    Filtreleri Temizle
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Email Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Email Listesi</CardTitle>
          </CardHeader>
          <CardContent>
            {emailLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz email kaydı bulunmuyor.</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Alıcı</TableHead>
                      <TableHead>Konu</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Gönderim Tarihi</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.map((emailLog) => (
                      <TableRow key={emailLog.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{emailLog.to}</p>
                            <p className="text-sm text-gray-500">
                              {emailLog.user?.name || "N/A"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate">
                            {emailLog.subject}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(emailLog.status)}</TableCell>
                        <TableCell>{formatDate(emailLog.sentAt)}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDetailDialog({ open: true, emailLog })}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {emailLog.status === "FAILED" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResend(emailLog)}
                                disabled={resendMutation.isLoading}
                                className="text-blue-500 hover:text-blue-600"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-500">
                      Sayfa {pagination.page} / {pagination.totalPages} (Toplam:{" "}
                      {pagination.total})
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                      >
                        Önceki
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                      >
                        Sonraki
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onOpenChange={(open) => setDetailDialog({ open, emailLog: detailDialog.emailLog })}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Detayı</DialogTitle>
          </DialogHeader>

          {detailDialog.emailLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Alıcı</Label>
                  <p className="font-medium">{detailDialog.emailLog.to}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Durum</Label>
                  <div>{getStatusBadge(detailDialog.emailLog.status)}</div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Gönderim Tarihi</Label>
                  <p className="font-medium">{formatDate(detailDialog.emailLog.sentAt)}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Kullanıcı</Label>
                  <p className="font-medium">{detailDialog.emailLog.user?.name || "N/A"}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Konu</Label>
                <p className="font-medium">{detailDialog.emailLog.subject}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-600">İçerik</Label>
                <div
                  className="mt-2 p-4 border rounded bg-gray-50 max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: detailDialog.emailLog.body }}
                />
              </div>

              {detailDialog.emailLog.error && (
                <div>
                  <Label className="text-sm text-red-600">Hata</Label>
                  <p className="text-sm text-red-500 mt-1">{detailDialog.emailLog.error}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetailDialog({ open: false, emailLog: null })}
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

